import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import SocialPlatformIcon, { getSocialPlatformConfig } from "../components/SocialPlatformIcon";
import { SOCIAL_PLATFORMS, socialPlatformLabel } from "../constants/socialPlatforms";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { canDeleteInPortal } from "./portalSession";

const ACCOUNT_STATUSES = ["Active", "Inactive"];
const EMPTY_FORM = {
  platformName: "",
  accountName: "",
  username: "",
  password: "",
  profileUrl: "",
  businessUrl: "",
  emailUsed: "",
  contactNumber: "",
  status: "Active",
  notes: "",
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function linkOrDash(url) {
  if (!url || String(url).trim() === "") return "—";
  const href = String(url).trim();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cportal-link">
      {href}
    </a>
  );
}

const SORT_ACCESSORS = {
  accountName: (a) => (a.Account_Name || "").toLowerCase(),
  platform: (a) => (a.Platform_Name || "").toLowerCase(),
  username: (a) => (a.Username || "").toLowerCase(),
  profileUrl: (a) => (a.Profile_URL || "").toLowerCase(),
  emailUsed: (a) => (a.Email_Used || "").toLowerCase(),
  contact: (a) => a.Contact_Number || "",
  status: (a) => (a.Status || "").toLowerCase(),
};

const COLUMNS = [
  { key: "accountName", label: "Account name" },
  { key: "platform", label: "Platform" },
  { key: "username", label: "Username" },
  { key: "profileUrl", label: "Profile URL" },
  { key: "emailUsed", label: "Email used" },
  { key: "contact", label: "Contact" },
  { key: "status", label: "Status" },
];

function CustomerSocialAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("accountName");
  const [sortDir, setSortDir] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [deactivatingId, setDeactivatingId] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const loadAccounts = useCallback(async () => {
    const res = await axiosInstance.get("/client/social-accounts");
    setAccounts(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadAccounts();
      } catch (_) {
        if (active) setError("Could not load social accounts.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadAccounts]);

  const platformOptions = useMemo(() => {
    const fromData = Array.from(
      new Set(accounts.map((a) => a.Platform_Name).filter(Boolean))
    );
    const known = SOCIAL_PLATFORMS.map((p) => p.value);
    return Array.from(new Set([...known, ...fromData])).sort((a, b) =>
      socialPlatformLabel(a).localeCompare(socialPlatformLabel(b))
    );
  }, [accounts]);

  const filtered = useMemo(() => {
    let list = accounts;

    if (statusFilter) list = list.filter((acc) => acc.Status === statusFilter);
    if (platformFilter) list = list.filter((acc) => acc.Platform_Name === platformFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((acc) =>
        [
          acc.Account_Name,
          acc.Platform_Name,
          socialPlatformLabel(acc.Platform_Name),
          acc.Username,
          acc.Email_Used,
          acc.Contact_Number,
          acc.Profile_URL,
          acc.Business_URL,
          acc.Notes,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [accounts, search, statusFilter, platformFilter]);

  const hasActiveFilters = !!statusFilter || !!platformFilter || !!search;

  const clearFilters = () => {
    setStatusFilter("");
    setPlatformFilter("");
    setSearch("");
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const handleDeactivate = async (acc) => {
    if (acc.Status === "Inactive") return;
    const label = acc.Account_Name || acc.Username || "this account";
    if (!window.confirm(`Deactivate ${label}? Its status will be set to Inactive.`)) return;

    setDeactivatingId(acc.Account_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/social-accounts/${acc.Account_Id}`);
      await loadAccounts();
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate social account.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.platformName.trim() ||
      !form.accountName.trim() ||
      !form.username.trim() ||
      !form.password.trim()
    ) {
      setFormError("Platform, account name, username, and password are required.");
      return;
    }
    const contactError = mobileValidationMessage(form.contactNumber, { label: "Contact number" });
    if (contactError) {
      setFormError(contactError);
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/social-accounts", {
        Platform_Name: form.platformName.trim(),
        Account_Name: form.accountName.trim(),
        Username: form.username.trim(),
        Password: form.password.trim(),
        Profile_URL: form.profileUrl.trim() || null,
        Business_URL: form.businessUrl.trim() || null,
        Email_Used: form.emailUsed.trim() || null,
        Contact_Number: form.contactNumber.trim() || null,
        Status: form.status || "Active",
        Notes: form.notes.trim() || null,
      });
      await loadAccounts();
      closeAddModal();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create social account."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Social accounts</h1>
        <button
          type="button"
          className="cportal-btn cportal-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          Add account
        </button>
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

      <div className="cportal-filters">
        <div className="cportal-filter-row">
          <input
            className="cportal-search cportal-filter-search"
            type="text"
            placeholder="Search account, username, email, contact"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
            <option value="">All platforms</option>
            {platformOptions.map((p) => (
              <option key={p} value={p}>
                {socialPlatformLabel(p)}
              </option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {ACCOUNT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="cportal-clear-filters" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="cportal-card">
        <div className="cportal-card-head">
          <span className="cportal-muted">{sorted.length} account(s)</span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : accounts.length === 0 ? (
          <p className="cportal-muted">No social accounts linked to your company yet.</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No social accounts match your filters.</p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((acc) => (
                  <tr key={acc.Account_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/social-accounts/${acc.Account_Id}`)}
                      >
                        {display(acc.Account_Name)}
                      </button>
                    </td>
                    <td>
                      {getSocialPlatformConfig(acc.Platform_Name) ? (
                        <SocialPlatformIcon platform={acc.Platform_Name} size={18} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{display(acc.Username)}</td>
                    <td>{linkOrDash(acc.Profile_URL)}</td>
                    <td>{display(acc.Email_Used)}</td>
                    <td>{display(acc.Contact_Number)}</td>
                    <td>{display(acc.Status)}</td>
                    <td>
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/social-accounts/${acc.Account_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit account"
                          aria-label="Edit account"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => handleDeactivate(acc)}
                            disabled={
                              acc.Status === "Inactive" ||
                              deactivatingId === acc.Account_Id
                            }
                            title="Delete account"
                            aria-label="Delete account"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="cportal-modal-overlay" onClick={closeAddModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-social-account-title"
          >
            <div className="cportal-modal-header">
              <h3 id="add-social-account-title">Add social account</h3>
              <button type="button" onClick={closeAddModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleSubmit}>
              {formError && (
                <div className="cportal-alert cportal-alert-error">{formError}</div>
              )}
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Platform <span className="cportal-required">*</span>
                  </label>
                  <select
                    value={form.platformName}
                    onChange={(e) => setField("platformName", e.target.value)}
                    required
                  >
                    <option value="">Select platform</option>
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field">
                  <label>
                    Account name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.accountName}
                    onChange={(e) => setField("accountName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Username <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.username}
                    onChange={(e) => setField("username", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Password <span className="cportal-required">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Profile URL</label>
                  <input
                    type="url"
                    value={form.profileUrl}
                    onChange={(e) => setField("profileUrl", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Business URL</label>
                  <input
                    type="url"
                    value={form.businessUrl}
                    onChange={(e) => setField("businessUrl", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Email used</label>
                  <input
                    type="email"
                    value={form.emailUsed}
                    onChange={(e) => setField("emailUsed", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Contact number</label>
                  <input
                    value={form.contactNumber}
                    onChange={(e) => setField("contactNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {ACCOUNT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeAddModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerSocialAccounts;
